import { z } from 'zod';
import { AuditedFields, IsoDate, Uuid, Version } from './common.js';
/**
 * Moteur de taxes configurable (ADR-009, DATA_MODEL §3.6).
 * StayAfrica : aucune TVA (régime CME) ; retenue à la source 5 % optionnelle
 * calculée sur le montant remisé (D-012, R-012).
 */
export const TaxRuleKind = z.enum(['TAX', 'WITHHOLDING']);
/** Assiette de calcul : sous-total brut ou sous-total après remise. */
export const TaxBase = z.enum(['SUBTOTAL', 'DISCOUNTED_SUBTOTAL']);
export const TaxAppliesTo = z.enum(['ALL', 'ACCOMMODATION', 'SERVICES']);
export const TaxRule = z
    .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    kind: TaxRuleKind,
    /** Taux décimal : 0.05 = 5 %. */
    rate: z.number(),
    isInclusive: z.boolean(),
    base: TaxBase,
    /** Activable document par document (case « Appliquer la retenue à la source »). */
    isOptional: z.boolean(),
    appliesTo: TaxAppliesTo,
    validFrom: IsoDate.nullable(),
    validTo: IsoDate.nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    /** Verrou optimiste des modifications (`PATCH /tax-rules/:id`). */
    version: Version,
})
    .merge(AuditedFields);
/**
 * Création d'une règle fiscale (`tax.manage`). Une TVA future se déclare ainsi :
 * `{ code: 'TVA18', name: 'TVA (18 %)', kind: 'TAX', rate: 0.18, base: 'DISCOUNTED_SUBTOTAL' }`
 * — sans modification de code (D-049, T-12).
 */
export const CreateTaxRuleRequest = z.object({
    code: z
        .string()
        .trim()
        .min(2)
        .max(20)
        .regex(/^[A-Za-z0-9_-]+$/, 'Lettres, chiffres, tiret et souligné uniquement'),
    name: z.string().trim().min(2).max(120),
    kind: TaxRuleKind,
    /** Taux décimal strictement positif et au plus 1 (0.18 = 18 %). */
    rate: z.number().gt(0).max(1),
    isInclusive: z.boolean().default(false),
    base: TaxBase.default('SUBTOTAL'),
    isOptional: z.boolean().default(false),
    appliesTo: TaxAppliesTo.default('ALL'),
    validFrom: IsoDate.nullable().optional(),
    validTo: IsoDate.nullable().optional(),
    sortOrder: z.number().int().min(0).max(1000).default(0),
});
/**
 * Modification (`tax.manage`) : tout est révisable sauf `code` et `kind`, qui
 * identifient la règle dans les snapshots des documents émis (ADR-009).
 */
export const UpdateTaxRuleRequest = CreateTaxRuleRequest.omit({ code: true, kind: true })
    .partial()
    .extend({ isActive: z.boolean().optional(), version: Version });
export const ListTaxRulesQuery = z.object({
    kind: TaxRuleKind.optional(),
    includeInactive: z
        .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
        .transform((v) => v === true || v === 'true' || v === '1')
        .default(false),
});
//# sourceMappingURL=tax-rules.js.map